import { PanelLeft } from 'lucide-react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Toaster, toast } from 'sonner';
import AppSidebar, { PAGE_MODULES } from '@/components/layout/app-sidebar';
import StatusBar from '@/components/layout/status-bar';
import TitleBar from '@/components/layout/titlebar';
import UpdateReadyBanner from '@/components/layout/update-ready-banner';
import WorkspacesHost from '@/components/layout/workspaces-host';
import { PinSetupPrompt } from '@/components/auth/pin-setup-dialog';
import { FaceIntruderOverlay } from '@/components/auth/face-intruder-overlay';
import { registerFaceAuthPolicy } from '@/lib/face/face-auth-policy';
import { clearAuthSession, getAuthToken, getAuthUser } from '@/lib/api/token-store';
import { registerAuthRealtimeBridge } from '@/lib/realtime/auth-realtime-bridge';
import { registerDevtoolsLockBridge } from '@/lib/electron/devtools-lock';
import { registerNotesMainWindowBridge } from '@/lib/electron/notes-main-window-bridge';
import { registerIdleAutoLock, registerLockHotkey } from '@/lib/auth/session-lock';
import { subscribeOpenDashboard, subscribeOpenThreadMessage } from '@/lib/notifications/notification-navigation';
import { subscribeOpenCalendarAppointment } from '@/lib/calendar/calendar-navigation';
import { Button } from '@/components/ui/button';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { cn } from '@/lib/utils';
import { useUserPreferences } from '@/hooks/use-user-preferences';
import { usePermissions } from '@/hooks/use-permissions';
import { useWorkspaces } from '@/hooks/use-workspaces';
import { hasPermission, refreshPermissions } from '@/lib/stores/permissions-store';
import {
  registerWorkspaceAuthBridge,
  removePageFromActiveWorkspace,
  setActiveWorkspacePage,
} from '@/lib/stores/workspace-store';
import { setUserPreferences } from '@/lib/stores/user-preference-store';
import { getTheme, setTheme, subscribeTheme, toggleTheme } from '@/lib/stores/theme-store';
import { isWebMode } from '@/lib/config/web-mode';

// Device mirror of the account theme — the theme-store owns account persistence, while the
// effect below keeps this copy current so the lock screen matches the last signed-in user.
const THEME_STORAGE_KEY = 'healo-theme';

// Sidebar rail geometry for the resizable shell: collapsed matches the old fixed w-20 icon
// rail, the expanded default matches the old fixed w-72. Dragging the handle below the min
// width snaps the panel down to the collapsed rail (same behavior as the threads panel).
const SIDEBAR_COLLAPSED_WIDTH_PX = 80;
const SIDEBAR_EXPANDED_WIDTH = '288px';

// Only VISIBLE dialogs matter: frozen panes' overlays sit display:none in their pane
// containers with dismissal listeners unmounted — an Escape can never close those, and
// they must not block navigation (they hide with their pane; the visible pane swap is
// safe regardless of them).
const hasVisibleOpenDialog = () =>
  [...document.querySelectorAll('[role="dialog"][data-state="open"]')].some(
    (el) => el.checkVisibility?.() ?? true,
  );

// Shared by every deep link that force-navigates the workspace (notification click,
// "show on calendar"). An open modal dialog would trap focus over the navigation, so ask
// it to close first (Escape) — if it refuses (close-guarded, e.g. unsaved edits), skip
// navigating: the target stays buffered in its navigation module and drains when the
// target page next shows.
function navigateToPage(targetPage) {
  if (!hasVisibleOpenDialog()) {
    setActiveWorkspacePage(targetPage);
    return;
  }
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
  );
  setTimeout(() => {
    if (hasVisibleOpenDialog()) return;
    setActiveWorkspacePage(targetPage);
  }, 0);
}

/**
 * The nav rail, mounted per signed-in user (App keys it on the active uid) because
 * `sidebarCollapsed` is an ACCOUNT preference: the panel's width has to start from the
 * owner's own choice, not from whoever was signed in before. `defaultSize` is frozen in
 * state for the lifetime of that mount — it only matters for the initial layout, and a
 * stable reference keeps collapse/expand from re-registering the panel.
 */
function SidebarPanel({ panelRef, collapsed, activePage, onPageSelect, onResize }) {
  const [initialSize] = useState(() =>
    collapsed ? `${SIDEBAR_COLLAPSED_WIDTH_PX}px` : SIDEBAR_EXPANDED_WIDTH,
  );
  return (
    <ResizablePanel
      panelRef={panelRef}
      collapsible
      collapsedSize={`${SIDEBAR_COLLAPSED_WIDTH_PX}px`}
      defaultSize={initialSize}
      minSize="200px"
      maxSize="400px"
      onResize={onResize}
    >
      <AppSidebar collapsed={collapsed} activePage={activePage} onPageSelect={onPageSelect} />
    </ResizablePanel>
  );
}

function App() {
  const [areDevToolsOpen, setAreDevToolsOpen] = useState(false);
  const lastUpdaterDebugTsRef = useRef(0);
  // Account preference (travels with the user, not the device), so the sidebar reopens in
  // the same expanded/collapsed mode this user left it in.
  const isSidebarCollapsed = useUserPreferences().sidebarCollapsed;
  const sidebarPanelRef = useRef(null);
  // Workspace store drives everything the old activePage/activeUserId state used to: which
  // user's frozen tree is active, and which of their keep-alive panes is the visible one.
  const { activeUserId, order, workspaces } = useWorkspaces();
  const isLoggedIn = Boolean(activeUserId);
  const activePage = workspaces[activeUserId]?.activePage || 'Dashboard';
  // The active page portals its toolbar (search, primary actions) into this header node, so the
  // shell needs just one slim bar instead of stacking a second per-page header row beneath it.
  const [headerSlotEl, setHeaderSlotEl] = useState(null);
  // Same idea for the titlebar: pages portal lightweight status text (Messages' thread counter)
  // into a slot next to the DevTools / theme buttons.
  const [titlebarSlotEl, setTitlebarSlotEl] = useState(null);
  // Account theme with device/OS fallback; re-reads on login/profile switch so each user's
  // stored choice applies the moment their session activates.
  const theme = useSyncExternalStore(subscribeTheme, getTheme);

  const handleToggleTheme = () => {
    toggleTheme();
  };

  // Drag and the header button both end up here (the panel reports every width change):
  // mirror the panel's width into the persisted preference so the rail mode drives
  // AppSidebar's rendering and survives restarts. Don't compare against state captured in
  // this closure — the store already no-ops when the value is unchanged.
  const handleSidebarResize = (panelSize) => {
    if (panelSize.inPixels > 0) {
      setUserPreferences({
        sidebarCollapsed: panelSize.inPixels <= SIDEBAR_COLLAPSED_WIDTH_PX + 1,
      });
    }
  };

  const handleToggleSidebar = () => {
    const panel = sidebarPanelRef.current;
    if (!panel) {
      setUserPreferences({ sidebarCollapsed: !isSidebarCollapsed });
      return;
    }
    if (isSidebarCollapsed) {
      panel.expand();
      // expand() no-ops when the panel mounted directly at its collapsed size, so force the width.
      if (panel.getSize().inPixels <= SIDEBAR_COLLAPSED_WIDTH_PX + 1) {
        panel.resize(SIDEBAR_EXPANDED_WIDTH);
      }
    } else {
      panel.collapse();
    }
  };

  const handleToggleDevTools = async () => {
    const nextState = await window.electronAPI?.toggleDevTools?.();
    if (typeof nextState === 'boolean') {
      setAreDevToolsOpen(nextState);
      return;
    }

    setAreDevToolsOpen((currentState) => !currentState);
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    // Follow the theme with the Dock / taskbar icon: light tile (light) vs navy tile (dark).
    window.electronAPI?.setThemeIcon?.(theme);
  }, [theme]);

  // Web mode: the browser-tab title tracks the signed-in user ("Name - Clarimed", plain "Clarimed"
  // on the lock screen). Electron keeps its productName window title untouched.
  useEffect(() => {
    if (!isWebMode()) return;
    const user = getAuthUser();
    const name = String(user?.name || user?.profile?.displayName || '').trim();
    document.title = activeUserId && name ? `${name} - Clarimed` : 'Clarimed';
  }, [activeUserId]);

  useEffect(() => {
    registerAuthRealtimeBridge();
    // Workspace activation/freeze rides the single auth-state event (login, PIN switch,
    // logout, 401) — one bridge covers every path.
    registerWorkspaceAuthBridge();
    // DevTools stay locked while no one is signed in, closing the un-hide-a-frozen-tree
    // loophole on the lock screen. Electron-only IPC — web mode has neither the lock nor
    // frozen trees to protect (workspaces-host unmounts instead of hiding there).
    if (!isWebMode()) {
      registerDevtoolsLockBridge();
      // My Notes window relays (auth session + notes WS pings → main process → notes
      // window). Main-window-only by design — see the bridge's comment.
      registerNotesMainWindowBridge();
    }
    // Idle auto-lock (server-configured minutes, 0 = off): inactivity clears the session,
    // which freezes workspaces onto the lock screen — same path as a manual Lock or a 401.
    registerIdleAutoLock();
    // Cmd/Ctrl+L locks on demand from anywhere — same freeze-the-workspace path as the manual
    // Lock button and the idle timer.
    registerLockHotkey();
    // Face unlock: recognizes who is at the monitor and signs them in / switches / locks.
    // Opens nothing (no camera, no 10MB of models) until the preference is on and a face is
    // enrolled. Desktop-only: web mode never registers the policy, so the whole face/camera
    // pipeline (watcher, getUserMedia, Human models) is unreachable there.
    if (!isWebMode()) {
      registerFaceAuthPolicy();
    }
    // Presence needs no app-side loop anymore: the backend derives online state from the
    // WebSocket connection itself and pushes snapshots/deltas that presence-service stores.
  }, []);

  // Clicking a new-message notification asks us to show the Messages page — or Chats when
  // the target is an internal thread (target.scope) — the messages hook owns opening the
  // thread and revealing the message.
  useEffect(
    () =>
      subscribeOpenThreadMessage((target) => {
        navigateToPage(
          target?.scope === 'internal'
            ? 'Chats'
            : target?.scope === 'direct'
              ? 'Private'
              : 'Messages',
        );
      }),
    [],
  );

  // Double-clicking an appointment in the Messages scheduling panel asks us to show the
  // Calendar page; the calendar owns picking the day, scrolling to the appointment and
  // highlighting it (see calendar-navigation.js).
  useEffect(() => subscribeOpenCalendarAppointment(() => navigateToPage('Calendar')), []);

  // Clicking an announcement toast/notification asks us to show the Dashboard, where the
  // feed marks the new posts itself.
  useEffect(() => subscribeOpenDashboard(() => navigateToPage('Dashboard')), []);

  // RBAC page gate: pages map to function-matrix modules (PAGE_MODULES). Reading the store
  // here re-renders on permission changes; the effect below also snaps the workspace back to
  // Dashboard when the open page becomes forbidden (rights revoked mid-session, or a
  // notification force-navigated somewhere the user can't see) — dropping the forbidden
  // pane so it unmounts instead of lingering frozen.
  const permissionsState = usePermissions();
  useEffect(() => {
    const requiredModule = PAGE_MODULES[activePage];
    if (requiredModule && !hasPermission(requiredModule, 'view')) {
      removePageFromActiveWorkspace(activePage);
      setActiveWorkspacePage('Dashboard');
    }
  }, [activePage, permissionsState]);

  // Gate ONLY the visible pane: hidden panes belong to (possibly other users') frozen
  // workspaces and must never be evaluated against the current user's permissions — that
  // would unmount them. This covers the frames before the snap effect above lands.
  const requiredModule = PAGE_MODULES[activePage];
  const effectiveActivePage =
    requiredModule && !hasPermission(requiredModule, 'view') ? 'Dashboard' : activePage;

  // A leftover toast from user A's session is PHI in front of user B — drop them all the
  // moment the active workspace changes (also fires on freeze/logout, which is fine).
  useEffect(() => {
    toast.dismiss();
  }, [activeUserId]);

  // 403 bridge: any API call denied by the backend surfaces one (throttled) toast and
  // refreshes permissions so revoked rights promptly disappear from the nav.
  useEffect(() => {
    let lastToastAt = 0;
    const onDenied = () => {
      const now = Date.now();
      if (now - lastToastAt > 3000) {
        lastToastAt = now;
        toast.error("You don't have permission to do that.");
      }
      refreshPermissions();
    };
    window.addEventListener('healo-permission-denied', onDenied);
    return () => window.removeEventListener('healo-permission-denied', onDenied);
  }, []);

  // 401 bridge (mirrors the 403 one): an expired/invalid session clears itself, which freezes
  // the workspace and lands on the roster lock screen — so expiry naturally becomes a PIN
  // re-entry that thaws the same tree. Login/PIN endpoints never dispatch this event (see
  // client.js), so a wrong password or PIN can't tear down the active session.
  useEffect(() => {
    let lastToastAt = 0;
    const onExpired = (event) => {
      const activeToken = getAuthToken();
      if (!activeToken) return;
      // A 401 produced by a DIFFERENT token than the active one is a straggler from the
      // previous user's in-flight request racing a profile switch — the new session is fine.
      const requestToken = String(event?.detail?.requestToken || '').trim();
      if (requestToken && requestToken !== activeToken) return;
      clearAuthSession();
      const now = Date.now();
      if (now - lastToastAt > 3000) {
        lastToastAt = now;
        toast.error('Your session has expired. Please sign in again.');
      }
    };
    window.addEventListener('healo-auth-expired', onExpired);
    return () => window.removeEventListener('healo-auth-expired', onExpired);
  }, []);

  useEffect(() => {
    const syncDevToolsState = async () => {
      const nextState = await window.electronAPI?.isDevToolsOpen?.();
      setAreDevToolsOpen(Boolean(nextState));
    };

    syncDevToolsState();

    const unsubscribe = window.electronAPI?.onDevToolsChanged?.((isOpen) => {
      setAreDevToolsOpen(Boolean(isOpen));
    });

    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    let isUnmounted = false;

    const handleUpdaterStatus = (payload) => {
      if (isUnmounted) {
        return;
      }
      const status = String(payload?.status || '');
      // "downloaded" is handled by the persistent UpdateReadyBanner — no toast.
      if (status === 'available') {
        toast.info(payload?.message || 'Update found. Downloading...');
      }
      if (status === 'not-available' && payload?.checkTrigger === 'manual') {
        toast.message(payload?.message || 'You are on the latest version.');
      }
      if (status === 'error') {
        toast.error(payload?.message || 'Auto-update failed.');
      }
    };

    const unsubscribe = window.electronAPI?.onUpdaterStatus?.(handleUpdaterStatus);

    return () => {
      isUnmounted = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    let isUnmounted = false;

    const printUpdaterDebug = (payload) => {
      if (isUnmounted) {
        return;
      }
      const ts = Number(payload?.ts || 0);
      if (ts && ts <= lastUpdaterDebugTsRef.current) {
        return;
      }
      if (ts) {
        lastUpdaterDebugTsRef.current = ts;
      }
      const isoTs = ts ? new Date(ts).toISOString() : new Date().toISOString();
      const message = String(payload?.message || '');
      console.info(`[updater-main ${isoTs}] ${message}`);
    };

    const unsubscribe = window.electronAPI?.onUpdaterDebugLog?.(printUpdaterDebug);

    const syncHistory = async () => {
      const history = await window.electronAPI?.getUpdaterDebugLogs?.();
      if (isUnmounted || !Array.isArray(history)) {
        return;
      }
      history.forEach(printUpdaterDebug);
    };

    void syncHistory();

    return () => {
      isUnmounted = true;
      unsubscribe?.();
    };
  }, []);

  return (
    <div className="app-window">
      {/* Web mode has no titlebar at all — brand lives in the sidebar banner, and nothing
          page-critical portals here (titlebarSlotEl stays null; PagePane's slot portal is
          null-guarded). Content starts at the very top of the tab. */}
      {!isWebMode() && (
        <TitleBar
          theme={theme}
          onToggleTheme={handleToggleTheme}
          onToggleDevTools={handleToggleDevTools}
          slotRef={setTitlebarSlotEl}
        />
      )}

      <div className="app-shell">
        <ResizablePanelGroup orientation="horizontal">
          {/* Nobody signed in = nobody's preferences: the rail's width belongs to the
              panel while its expanded/collapsed rendering belongs to the ACCOUNT
              preference, so on the lock screen the two can only disagree (last user's
              width, default user's rendering). Drop the rail — and the chrome around it —
              until a session owns them again. The key remounts the panel per user so each
              one's width starts from their own preference on login / profile switch. */}
          {isLoggedIn ? (
            <>
              <SidebarPanel
                key={activeUserId}
                panelRef={sidebarPanelRef}
                collapsed={isSidebarCollapsed}
                activePage={effectiveActivePage}
                onPageSelect={setActiveWorkspacePage}
                onResize={handleSidebarResize}
              />
              <ResizableHandle withHandle />
            </>
          ) : null}
          <ResizablePanel>
            <section className="flex h-full min-h-0 min-w-0 flex-col">
              {/* Hidden rather than unmounted on the lock screen: the header div below is
                  every pane's portal target, and swapping its identity on each lock would
                  re-render all the frozen trees hanging off it. */}
              <header
                className={cn(
                  'flex h-12 items-center gap-2.5 border-b border-border px-3',
                  !isLoggedIn && 'hidden',
                )}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={handleToggleSidebar}
                  title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  <PanelLeft className="h-4 w-4" />
                </Button>
                {/* Each keep-alive pane owns a child div in here (portaled by WorkspacesHost)
                    and portals its search box / primary actions into its own div; the host
                    toggles which one is displayed in lockstep with pane visibility. */}
                <div ref={setHeaderSlotEl} className="flex min-w-0 flex-1 items-center gap-2" />
              </header>

              <main className="min-w-0 flex-1 overflow-auto">
                <WorkspacesHost
                  activeUserId={activeUserId}
                  order={order}
                  workspaces={workspaces}
                  isLoggedIn={isLoggedIn}
                  effectiveActivePage={effectiveActivePage}
                  theme={theme}
                  onSelectTheme={setTheme}
                  headerSlotEl={headerSlotEl}
                  titlebarSlotEl={titlebarSlotEl}
                />
              </main>
            </section>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      <StatusBar />
      {!isLoggedIn ? <UpdateReadyBanner variant="floating" /> : null}
      {/* First-login PIN offer; mounted outside the workspaces so it survives the workspace
          activation the login itself triggers. */}
      <PinSetupPrompt />
      {/* "Someone else is at this machine" warning + lock countdown. Outside the workspaces for
          the same reason, and so it covers every frozen tree rather than one pane. */}
      <FaceIntruderOverlay />
      <Toaster theme={theme} richColors position="bottom-right" closeButton duration={3200} />
    </div>
  );
}

export default App;